// src/services/technicalAnalysisService.ts
import { marketDataService } from './marketData/marketDataService';

export interface TechnicalIndicator {
  value: number;
  signal: 'Bullish' | 'Neutral' | 'Bearish';
  color: string;
}

export interface TechnicalAnalysisData {
  timeFrame: string;
  indicatorType: string;
  indicators: {
    rsi: TechnicalIndicator;
    macd: TechnicalIndicator;
    stochastic: TechnicalIndicator;
  };
  chartData: any;
  analysisText: string;
}

/**
 * Calculates the RSI value based on historical price data
 * @param prices Array of closing prices (most recent first)
 * @param period RSI period (default: 14)
 * @returns RSI value and signal
 */
const calculateRSI = (prices: number[], period: number = 14): TechnicalIndicator => {
  if (prices.length < period + 1) {
    return { value: 50, signal: 'Neutral', color: '#FFA500' };
  }

  // Calculate price changes
  const changes = [];
  for (let i = 1; i < prices.length; i++) {
    changes.push(prices[i - 1] - prices[i]);
  }

  // Calculate gains and losses
  let gains = 0;
  let losses = 0;

  for (let i = 0; i < period; i++) {
    if (changes[i] >= 0) {
      gains += changes[i];
    } else {
      losses += Math.abs(changes[i]);
    }
  }

  // Average gains and losses
  let avgGain = gains / period;
  let avgLoss = losses / period;

  // Calculate RS and RSI
  const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
  const rsi = 100 - (100 / (1 + rs));

  // Determine signal based on RSI value
  let signal: 'Bullish' | 'Neutral' | 'Bearish';
  let color: string;

  if (rsi > 70) {
    signal = 'Bearish';
    color = '#FF0000';
  } else if (rsi < 30) {
    signal = 'Bullish';
    color = '#008000';
  } else {
    signal = 'Neutral';
    color = '#FFA500';
  }

  return {
    value: parseFloat(rsi.toFixed(1)),
    signal,
    color
  };
};

/**
 * Calculate MACD based on historical price data
 * @param prices Array of closing prices (most recent first)
 * @returns MACD value and signal
 */
const calculateMACD = (prices: number[]): TechnicalIndicator => {
  if (prices.length < 26) {
    return { value: 0, signal: 'Neutral', color: '#FFA500' };
  }

  // Calculate EMAs
  const ema12 = calculateEMA(prices, 12);
  const ema26 = calculateEMA(prices, 26);

  // Calculate MACD Line
  const macdLine = ema12 - ema26;

  // Determine signal
  let signal: 'Bullish' | 'Neutral' | 'Bearish';
  let color: string;

  if (macdLine > 0.5) {
    signal = 'Bullish';
    color = '#008000';
  } else if (macdLine < -0.5) {
    signal = 'Bearish';
    color = '#FF0000';
  } else {
    signal = 'Neutral';
    color = '#FFA500';
  }

  return {
    value: parseFloat(macdLine.toFixed(2)),
    signal,
    color
  };
};

/**
 * Calculate Stochastic oscillator based on historical price data
 * @param prices Array of objects with high, low, and close prices (most recent first)
 * @returns Stochastic value and signal
 */
const calculateStochastic = (prices: any[]): TechnicalIndicator => {
  if (prices.length < 14) {
    return { value: 50, signal: 'Neutral', color: '#FFA500' };
  }

  const period = 14;
  const smoothK = 3;

  // Find lowest low and highest high over the period
  let lowestLow = Number.MAX_VALUE;
  let highestHigh = Number.MIN_VALUE;

  for (let i = 0; i < period; i++) {
    const high = prices[i].high;
    const low = prices[i].low;

    if (low < lowestLow) lowestLow = low;
    if (high > highestHigh) highestHigh = high;
  }

  // Calculate the %K
  const currentClose = prices[0].close;
  const stochValue = ((currentClose - lowestLow) / (highestHigh - lowestLow)) * 100;

  // Determine signal
  let signal: 'Bullish' | 'Neutral' | 'Bearish';
  let color: string;

  if (stochValue > 80) {
    signal = 'Bearish';
    color = '#FF0000';
  } else if (stochValue < 20) {
    signal = 'Bullish';
    color = '#008000';
  } else {
    signal = 'Neutral';
    color = '#FFA500';
  }

  return {
    value: parseFloat(stochValue.toFixed(1)),
    signal,
    color
  };
};

/**
 * Calculate EMA (Exponential Moving Average)
 * @param prices Array of closing prices (most recent first)
 * @param period EMA period
 * @returns EMA value
 */
const calculateEMA = (prices: number[], period: number): number => {
  const k = 2 / (period + 1);
  let ema = prices[prices.length - 1];

  for (let i = prices.length - 2; i >= 0; i--) {
    ema = prices[i] * k + ema * (1 - k);
  }

  return ema;
};

/**
 * Generate analysis text based on technical indicators
 */
const generateAnalysisText = (symbol: string, rsi: TechnicalIndicator, macd: TechnicalIndicator, stochastic: TechnicalIndicator): string => {
  const signals = {
    bullish: 0,
    neutral: 0,
    bearish: 0
  };

  // Count signals
  [rsi, macd, stochastic].forEach(indicator => {
    if (indicator.signal === 'Bullish') signals.bullish++;
    else if (indicator.signal === 'Neutral') signals.neutral++;
    else signals.bearish++;
  });

  // Determine overall sentiment
  let overallSentiment = 'mixed signals';
  if (signals.bullish >= 2) overallSentiment = 'bullish signals';
  else if (signals.bearish >= 2) overallSentiment = 'bearish signals';

  // Generate text based on indicator values
  return `${symbol} is showing ${overallSentiment} with ${rsi.signal.toLowerCase()} RSI (${rsi.value}), ${macd.signal.toLowerCase()} MACD (${macd.value > 0 ? '+' : ''}${macd.value}), and ${stochastic.signal.toLowerCase()} Stochastic (${stochastic.value}). ${
    signals.bullish >= 2 
      ? 'The momentum appears to be positive, suggesting potential upward movement.'
      : signals.bearish >= 2
        ? 'The momentum appears to be negative, suggesting potential downward movement.'
        : 'The mixed signals suggest a period of consolidation may be likely.'
  }`;
};

export const technicalAnalysisService = {
  /**
   * Get technical analysis data for a symbol
   * @param symbol Stock symbol
   * @param timeFrame Time frame for analysis (1D, 1W, 1M, 1Y)
   * @returns Technical analysis data
   */
  getTechnicalAnalysis: async (symbol: string, timeFrame: string = '1D'): Promise<TechnicalAnalysisData> => {
    try {
      // Get quote data from marketDataService using Alpaca
      const quoteData = await marketDataService.getQuote(symbol, 'alpaca');
      
      // If we can't get data, use mock data
      if (!quoteData) {
        return getMockTechnicalAnalysis(symbol, timeFrame);
      }
      
      // Try to get historical data for indicators
      try {
        // Map timeFrame to appropriate interval for historical data
        let interval = '1Day';
        if (timeFrame === '1D') interval = '5Min';
        else if (timeFrame === '1W') interval = '1Hour';
        else if (timeFrame === '1M') interval = '1Day';
        else if (timeFrame === '1Y') interval = '1Day';
        
        // Calculate date range based on timeFrame
        const endDate = new Date().toISOString();
        let startDate = new Date();
        if (timeFrame === '1D') startDate.setDate(startDate.getDate() - 1);
        else if (timeFrame === '1W') startDate.setDate(startDate.getDate() - 7);
        else if (timeFrame === '1M') startDate.setMonth(startDate.getMonth() - 1);
        else if (timeFrame === '1Y') startDate.setFullYear(startDate.getFullYear() - 1);
        
        // Fetch historical data from Alpaca via marketDataService
        let historicalData;
        if (timeFrame === '1D') {
          historicalData = await marketDataService.getTimeSeriesIntraday(symbol, interval, 'alpaca');
        } else {
          historicalData = await marketDataService.getTimeSeriesDaily(symbol, 'alpaca');
        }
        
        if (historicalData && historicalData.length > 0) {
          // Sort by date (newest first)
          historicalData.sort((a, b) => new Date(b.timestamp || b.t).getTime() - new Date(a.timestamp || a.t).getTime());
          
          // Extract price data for calculations
          const closePrices = historicalData.map(bar => parseFloat(bar.close || bar.c));
          
          // Extract OHLC data for Stochastic calculation
          const ohlcData = historicalData.map(bar => ({
            open: parseFloat(bar.open || bar.o),
            high: parseFloat(bar.high || bar.h),
            low: parseFloat(bar.low || bar.l),
            close: parseFloat(bar.close || bar.c)
          }));
          
          // Calculate technical indicators
          const rsi = calculateRSI(closePrices);
          const macd = calculateMACD(closePrices);
          const stochastic = calculateStochastic(ohlcData);
          
          // Format data for charting (in the actual app implementation)
          const chartData = historicalData.map((bar, index) => {
            const timestamp = bar.timestamp || bar.t;
            return {
              time: new Date(timestamp).getTime(),
              open: parseFloat(bar.open || bar.o),
              high: parseFloat(bar.high || bar.h),
              low: parseFloat(bar.low || bar.l),
              close: parseFloat(bar.close || bar.c),
              volume: parseFloat(bar.volume || bar.v)
            };
          });
          
          // Generate analysis text
          const analysisText = generateAnalysisText(symbol, rsi, macd, stochastic);
          
          return {
            timeFrame,
            indicatorType: 'Momentum',
            indicators: {
              rsi,
              macd,
              stochastic
            },
            chartData,
            analysisText
          };
        } else {
          // Fall back to mock data if we don't have enough historical data
          return getMockTechnicalAnalysis(symbol, timeFrame);
        }
      } catch (err) {
        console.error('Error calculating technical indicators:', err);
        return getMockTechnicalAnalysis(symbol, timeFrame);
      }
    } catch (err) {
      console.error('Error in technical analysis service:', err);
      return getMockTechnicalAnalysis(symbol, timeFrame);
    }
  }
};

/**
 * Get mock technical analysis data when real data is unavailable
 */
const getMockTechnicalAnalysis = (symbol: string, timeFrame: string): TechnicalAnalysisData => {
  // Generate realistic mock indicator values with some randomization
  const mockRsi = {
    value: 50 + (Math.random() * 20 - 10),
    signal: 'Neutral',
    color: '#FFA500'
  } as TechnicalIndicator;
  
  const mockMacd = {
    value: (Math.random() * 0.8 - 0.4),
    signal: 'Neutral',
    color: '#FFA500'
  } as TechnicalIndicator;
  
  const mockStoch = {
    value: 50 + (Math.random() * 30 - 15),
    signal: 'Neutral',
    color: '#FFA500'
  } as TechnicalIndicator;
  
  // Adjust signals based on values
  if (mockRsi.value > 70) {
    mockRsi.signal = 'Bearish';
    mockRsi.color = '#FF0000';
  } else if (mockRsi.value < 30) {
    mockRsi.signal = 'Bullish';
    mockRsi.color = '#008000';
  }
  
  if (mockMacd.value > 0.2) {
    mockMacd.signal = 'Bullish';
    mockMacd.color = '#008000';
  } else if (mockMacd.value < -0.2) {
    mockMacd.signal = 'Bearish';
    mockMacd.color = '#FF0000';
  }
  
  if (mockStoch.value > 80) {
    mockStoch.signal = 'Bearish';
    mockStoch.color = '#FF0000';
  } else if (mockStoch.value < 20) {
    mockStoch.signal = 'Bullish';
    mockStoch.color = '#008000';
  }
  
  // Format values to match display
  mockRsi.value = parseFloat(mockRsi.value.toFixed(1));
  mockMacd.value = parseFloat(mockMacd.value.toFixed(2));
  mockStoch.value = parseFloat(mockStoch.value.toFixed(1));
  
  // Generate analysis text
  const analysisText = generateAnalysisText(symbol, mockRsi, mockMacd, mockStoch);
  
  return {
    timeFrame,
    indicatorType: 'Momentum',
    indicators: {
      rsi: mockRsi,
      macd: mockMacd,
      stochastic: mockStoch
    },
    chartData: null,
    analysisText
  };
};